import type { FC, ReactNode } from 'react'
import type { BlobShape } from '@/lib/shapes'

import { useEffect, useRef, useState } from 'react'
import { ArrowUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Blob } from '@/components/generals/blob'

const REVEAL_SELECTOR = '.reveal, .reveal-left, .reveal-right, .reveal-pop'
const INTERACTIVE_SELECTOR = 'a, button, select, label, [data-cursor]'
const SECTION_SELECTOR = 'section[id], footer'

// Only these sections stick and get covered by the next one; the rest scroll normally.
const STACKED_SECTIONS = ['top', 'clients', 'why-us', 'reviews', 'team']

interface CursorStyle {
    shape: BlobShape
    color: string
    label: string
}

// The cursor takes on a new shape, color and word in every section.
const CURSORS: Record<string, CursorStyle> = {
    top: { shape: 'burst', color: 'text-butter', label: 'Hi!' },
    clients: { shape: 'flower', color: 'text-mint', label: 'Wow' },
    services: { shape: 'cloud', color: 'text-peach', label: 'Ooh' },
    'why-us': { shape: 'flower', color: 'text-bubblegum', label: 'Yay' },
    work: { shape: 'blob', color: 'text-lavender', label: 'Peek' },
    process: { shape: 'gear', color: 'text-coral', label: 'Go!' },
    compare: { shape: 'burst', color: 'text-mint', label: 'Us!' },
    reviews: { shape: 'flower', color: 'text-butter', label: 'Aww' },
    pricing: { shape: 'cloud', color: 'text-sky', label: 'Deal' },
    team: { shape: 'blob', color: 'text-bubblegum', label: 'Hey' },
    faq: { shape: 'gear', color: 'text-lavender', label: 'Hmm' },
    contact: { shape: 'burst', color: 'text-butter', label: 'Say hi' },
    footer: { shape: 'flower', color: 'text-coral', label: 'Bye!' }
}

const DEFAULT_CURSOR: CursorStyle = { shape: 'blob', color: 'text-butter', label: '' }

// Site-wide motion: scroll reveals, a scroll progress bar, a cursor blob that
// grows over anything clickable, and a back-to-top button.
export const SiteMotion: FC = (): ReactNode => {
    const progressRef = useRef<HTMLDivElement>(null)
    const cursorRef = useRef<HTMLDivElement>(null)
    const [showTop, setShowTop] = useState(false)
    const [cursorOn, setCursorOn] = useState(false)
    const [cursorKey, setCursorKey] = useState('')

    // Scroll reveals. Elements already on screen are shown at once so nothing
    // flickers; the rest animate in as they scroll into view.
    useEffect(() => {
        const root = document.documentElement
        const seen = new WeakSet<Element>()

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return
                entry.target.classList.add('is-visible')
                observer.unobserve(entry.target)
            })
        }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' })

        const scan = (): void => {
            document.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
                if (seen.has(element)) return
                seen.add(element)

                const rect = element.getBoundingClientRect()
                const onScreen = rect.top < window.innerHeight && rect.bottom > 0

                if (onScreen && !root.classList.contains('reveal-ready')) element.classList.add('is-visible')
                else observer.observe(element)
            })
        }

        scan()
        root.classList.add('reveal-ready')

        const mutations = new MutationObserver(scan)
        mutations.observe(document.body, { childList: true, subtree: true })

        return () => {
            observer.disconnect()
            mutations.disconnect()
        }
    }, [])

    // Stacked sections: each homepage section sticks once its bottom reaches the
    // bottom of the screen, and the next section slides up over it. The covered
    // section dims a little as it disappears underneath.
    useEffect(() => {
        const all = Array.from(document.querySelectorAll<HTMLElement>('main > section'))
        const sections = all.filter((section, index) => STACKED_SECTIONS.includes(section.id) && index < all.length - 1)
        if (sections.length === 0) return

        let frame = 0

        const placeSections = (): void => {
            sections.forEach((section) => {
                section.style.setProperty('--stack-top', `${Math.min(0, window.innerHeight - section.offsetHeight)}px`)
            })
        }

        const shadeSections = (): void => {
            frame = 0
            sections.forEach((section) => {
                const next = section.nextElementSibling
                const covered = next ? (window.innerHeight - next.getBoundingClientRect().top) / window.innerHeight : 0
                section.style.setProperty('--covered', Math.min(Math.max(covered, 0), 1).toFixed(3))
            })
        }

        const onScroll = (): void => {
            if (!frame) frame = requestAnimationFrame(shadeSections)
        }

        const onResize = (): void => {
            placeSections()
            onScroll()
        }

        sections.forEach((section) => section.classList.add('stack-section'))
        placeSections()
        shadeSections()

        const resizes = new ResizeObserver(onResize)
        sections.forEach((section) => resizes.observe(section))
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onResize)

        return () => {
            resizes.disconnect()
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onResize)
            cancelAnimationFrame(frame)
            sections.forEach((section) => section.classList.remove('stack-section'))
        }
    }, [])

    // Scroll progress and back-to-top visibility.
    useEffect(() => {
        let frame = 0

        const update = (): void => {
            frame = 0
            const max = document.documentElement.scrollHeight - window.innerHeight
            const progress = max > 0 ? window.scrollY / max : 0

            if (progressRef.current) progressRef.current.style.transform = `scaleX(${progress})`
            setShowTop(window.scrollY > 900)
        }

        const onScroll = (): void => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            cancelAnimationFrame(frame)
        }
    }, [])

    // Cursor blob, only for a real mouse.
    useEffect(() => {
        if (!window.matchMedia('(pointer: fine)').matches) return

        setCursorOn(true)
        document.documentElement.classList.add('custom-cursor')

        const target = { x: -100, y: -100 }
        const current = { x: -100, y: -100, scale: 1 }
        let hovering = false
        let visible = false
        let frame = 0
        let lastKey = ''

        const tick = (): void => {
            current.x += (target.x - current.x) * 0.2
            current.y += (target.y - current.y) * 0.2
            current.scale += ((hovering ? 1.5 : 1) - current.scale) * 0.18

            const node = cursorRef.current
            if (node) {
                node.style.transform = `translate3d(${current.x}px, ${current.y}px, 0) translate(-50%, -50%) scale(${current.scale.toFixed(3)})`
                node.style.opacity = visible ? '1' : '0'
            }

            frame = requestAnimationFrame(tick)
        }

        const onMove = (event: PointerEvent): void => {
            if (event.pointerType !== 'mouse') return
            target.x = event.clientX
            target.y = event.clientY
            visible = true
            const element = event.target instanceof Element ? event.target : null
            hovering = Boolean(element?.closest(INTERACTIVE_SELECTOR))

            const section = element?.closest(SECTION_SELECTOR)
            const key = section ? (section.tagName === 'FOOTER' ? 'footer' : section.id) : ''
            if (key !== lastKey) {
                lastKey = key
                setCursorKey(key)
            }
        }

        const onLeave = (): void => {
            visible = false
        }

        frame = requestAnimationFrame(tick)
        window.addEventListener('pointermove', onMove, { passive: true })
        document.documentElement.addEventListener('pointerleave', onLeave)

        return () => {
            cancelAnimationFrame(frame)
            document.documentElement.classList.remove('custom-cursor')
            window.removeEventListener('pointermove', onMove)
            document.documentElement.removeEventListener('pointerleave', onLeave)
        }
    }, [])

    return (
        <>
            <div aria-hidden='true' className='pointer-events-none fixed inset-x-0 top-0 z-[60] h-1 bg-plum/10'>
                <div ref={progressRef} style={{ transform: 'scaleX(0)' }} className='h-full origin-left bg-linear-to-r from-coral via-bubblegum to-butter' />
            </div>

            {cursorOn && (
                <div
                    ref={cursorRef}
                    aria-hidden='true'
                    className='pointer-events-none fixed top-0 left-0 z-[70] opacity-0 transition-opacity duration-200 will-change-transform'
                >
                    <CursorShape key={cursorKey} style={CURSORS[cursorKey] ?? DEFAULT_CURSOR} />
                </div>
            )}

            <button
                type='button'
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                tabIndex={showTop ? 0 : -1}
                aria-hidden={!showTop}
                className={cn(
                    'fixed right-4 bottom-4 z-50 grid size-12 place-items-center rounded-full border-2 border-plum bg-butter text-plum shadow-[0_4px_0_0_var(--color-plum)] transition-all duration-300 hover:-translate-y-1 hover:animate-jelly sm:right-6 sm:bottom-6',
                    showTop ? 'translate-y-0 scale-100 opacity-100' : 'pointer-events-none translate-y-6 scale-50 opacity-0'
                )}
            >
                <ArrowUp className='size-5' aria-hidden='true' />
                <span className='sr-only'>Back to top</span>
            </button>
        </>
    )
}

interface CursorShapeProps {
    style: CursorStyle
}

const CursorShape: FC<CursorShapeProps> = ({ style }): ReactNode => {
    return (
        <span className='relative grid size-12 place-items-center animate-in zoom-in-0 spin-in-90 duration-300'>
            <Blob
                shape={style.shape}
                className={cn('absolute inset-0 size-full drop-shadow-[0_2px_0_rgba(60,13,37,0.6)] motion-safe:animate-spin-slow', style.color)}
            />

            {style.label && (
                <span className='relative font-heading text-[0.7rem] leading-none whitespace-nowrap text-plum'>
                    {style.label}
                </span>
            )}
        </span>
    )
}
